import { FC, useState, useEffect } from 'react';
import {
  Table,
  Input,
  Switch,
  Space,
  Typography,
  Dropdown,
  Button,
  Card,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  SearchOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useGet } from '../../../hooks/useApi';
import SidebarPage from '../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../layout/PageTitle/PageTitle';
import {
  Family,
  FamiliesListResponse,
  FamiliesListParams,
} from '../../../models/admin/families.model';
import debounce from 'lodash/debounce';

const { Text } = Typography;

const FamiliesList: FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState<FamiliesListParams>({
    page: 1,
    limit: 20,
    search: '',
    includeUsers: false,
    limitToContext: false,
  });

  // Role-based visibility state
  const [isWebAdmin, setIsWebAdmin] = useState<boolean>(false);
  const [isSiteDirector, setIsSiteDirector] = useState<boolean>(false);

  // Check user access context from localStorage
  useEffect(() => {
    const checkAccessContext = () => {
      const auth = localStorage.getItem('auth');
      if (auth) {
        try {
          const authObj = JSON.parse(auth);
          const accessContextId = authObj.selectedAccessContextId;
          // Site Director context = 1
          setIsSiteDirector(accessContextId === 1);
          // Web Admin context = 3
          setIsWebAdmin(accessContextId === 3);
        } catch {
          setIsSiteDirector(false);
          setIsWebAdmin(false);
        }
      }
    };
    checkAccessContext();
  }, []);

  const { data, isLoading, isError } = useGet<FamiliesListResponse>(
    '/v1/auth/admin/families',
    {
      params,
      queryKey: ['admin-families', params],
    }
  );

  const handleSearch = debounce((value: string) => {
    setParams((p) => ({ ...p, search: value, page: 1 }));
  }, 500);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setParams((p) => ({
      ...p,
      page: pagination.current || 1,
      limit: Math.min(pagination.pageSize || 20, 100),
    }));
  };

  const handleIncludeUsersToggle = (checked: boolean) => {
    setParams((p) => ({ ...p, includeUsers: checked, page: 1 }));
  };

  const handleLimitToContextToggle = (checked: boolean) => {
    setParams((p) => ({ ...p, limitToContext: checked, page: 1 }));
  };

  const getRowActions = (record: Family): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'family-info',
        icon: <TeamOutlined />,
        label: 'Family Info',
        onClick: () => navigate(`/admin/families/${record.id}`),
      },
      {
        key: 'family-ledger',
        icon: <DollarOutlined />,
        label: 'Family Ledger',
        onClick: () => navigate(`/admin/families/${record.id}/ledger`),
      },
    ];

    if (record.user?.id) {
      items.push({
        key: 'user-info',
        icon: <UserOutlined />,
        label: 'User Info',
        onClick: () => navigate(`/admin/users/${record.user?.id}`),
      });
    }

    if (record.students && record.students.length > 0) {
      items.push({
        type: 'divider',
      });
      items.push({
        key: 'students-header',
        label: 'Students',
        type: 'group',
        children: record.students.map((student) => ({
          key: `student-${student.id}`,
          icon: <UserOutlined />,
          label: `${student.firstName} ${student.lastName}`,
          onClick: () => navigate(`/admin/students/${student.id}`),
        })),
      });
    }

    return items;
  };

  const columns: ColumnsType<Family> = [
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Primary Name',
      key: 'name',
      render: (_: unknown, record: Family) =>
        `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Students',
      dataIndex: 'students',
      key: 'students',
      render: (students: Family['students']) => students?.length || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: Family) => (
        <Dropdown menu={{ items: getRowActions(record) }} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (isError) {
    return (
      <SidebarPage>
        <PageTitle>STAR Families</PageTitle>
        <Text type="danger">Error loading families. Please try again.</Text>
      </SidebarPage>
    );
  }

  return (
    <SidebarPage>
      <PageTitle>STAR Families</PageTitle>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card size="small">
          <Space wrap>
            <Link to="/admin/families/delinquent">
              <Button icon={<DollarOutlined />}>Delinquent Accounts</Button>
            </Link>
            <Link to="/admin/families/credit">
              <Button icon={<DollarOutlined />}>Credit Accounts</Button>
            </Link>
            {isWebAdmin && (
              <>
                <Link to="/admin/users/search">
                  <Button icon={<SearchOutlined />}>User Deep Search</Button>
                </Link>
                <Link to="/admin/families/elop/create">
                  <Button icon={<PlusOutlined />}>Create ELO-P Accounts</Button>
                </Link>
              </>
            )}
          </Space>
        </Card>

        <Space wrap>
          <Input.Search
            placeholder="Search by email, name..."
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 300 }}
          />

          {isWebAdmin && (
            <Space>
              <Text>Show All Users:</Text>
              <Switch
                checked={params.includeUsers}
                onChange={handleIncludeUsersToggle}
              />
            </Space>
          )}

          {isSiteDirector && (
            <Space>
              <Text>Show Galaxy Only:</Text>
              <Switch
                checked={params.limitToContext}
                onChange={handleLimitToContextToggle}
              />
            </Space>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={data?.data || []}
          loading={isLoading}
          rowKey="id"
          onChange={handleTableChange}
          pagination={{
            current: params.page,
            pageSize: params.limit,
            total: data?.pagination?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} families`,
          }}
        />
      </Space>
    </SidebarPage>
  );
};

export default FamiliesList;
